import { useCallback, useState } from 'react';

import { IconsEnum } from '../../../../assets/icons/types';
import { Popover } from '../../../../helpers/Popover';
import { PureTooltipIconRefButtonHelper } from '../../../../helpers/buttons/PureTooltipIconRefButtonHelper';
import { useDropdownState } from '../../../../helpers/dropdowns/hooks/useDropdownState';
import { PopoverPlacement } from '../../../../helpers/Popover/popoverConstants';
import { TooltipPlacement } from '../../../../helpers/tooltips/tooltipsConstants';

import { ComposerUnits } from '../../composerTypes';

import { DisplaySettingsMainPanel } from './components/DisplaySettingsMainPanel';
import { DisplaySettingsUnitsPanel } from './components/DisplaySettingsUnitsPanel';

type Panel = 'main' | 'units';

interface ComposerDisplaySettingsButtonProps {
  buttonClassName?: string;
  showDimensions: boolean;
  onToggleDimensions: () => void;
  units: ComposerUnits;
  onSelectUnits: (units: ComposerUnits) => void;
}

function ComposerDisplaySettingsButton({
  buttonClassName,
  showDimensions,
  onToggleDimensions,
  units,
  onSelectUnits
}: ComposerDisplaySettingsButtonProps) {
  const [panel, setPanel] = useState<Panel>('main');

  const {
    isPopoverOpen,
    referencePopoverElement,
    referencePopoverCallbackRef,
    togglePopover,
    closePopover
  } = useDropdownState();

  const handleClose = useCallback(() => {
    setPanel('main');
    closePopover();
  }, [closePopover]);

  const handleToggle = useCallback(() => {
    if (!isPopoverOpen) {
      setPanel('main');
    }
    togglePopover();
  }, [isPopoverOpen, togglePopover]);

  const unitsLabel = units === ComposerUnits.CM ? 'cm' : 'in';

  return (
    <div className="relative inline-flex">
      <PureTooltipIconRefButtonHelper
        ref={referencePopoverCallbackRef}
        icon={IconsEnum.ADJUSTMENTS_ALT_SOLID}
        iconClassName="h-5 w-5"
        className={
          buttonClassName ||
          'py-2 pl-2 pr-2 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-6 focus:ring-base bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600'
        }
        tooltipText="Display settings"
        tooltipPlacement={TooltipPlacement.TOP}
        onClick={handleToggle}
      />
      {isPopoverOpen && (
        <Popover
          referenceElement={referencePopoverElement}
          placement={PopoverPlacement.TOP_START}
          distanceOffset={6}
          className="min-w-48 lg:min-w-60 z-50 overflow-y-auto bg-white dark:bg-gray-700 dark:text-white border border-transparent focus:outline-none py-1 ring-1 ring-black ring-opacity-5 rounded-md shadow-lg text-gray-700 sm:max-w-80"
          closeOnOuterClick
          closePopover={handleClose}
        >
          {panel === 'main' && (
            <DisplaySettingsMainPanel
              showDimensions={showDimensions}
              onToggleDimensions={onToggleDimensions}
              unitsLabel={unitsLabel}
              onOpenUnits={() => setPanel('units')}
            />
          )}
          {panel === 'units' && (
            <DisplaySettingsUnitsPanel
              units={units}
              onBack={() => setPanel('main')}
              onSelect={onSelectUnits}
            />
          )}
        </Popover>
      )}
    </div>
  );
}

export default ComposerDisplaySettingsButton;
